/**
 * Labstep
 *
 * @module grid/Report/coldefs
 * @desc Grid Report Column Definitions
 */

import type { ValueGetterParams } from 'ag-grid-community';
import { EntityLinkComponent } from 'labstep-web/components/Entity/Link/Component';
import Profile from 'labstep-web/components/User/Show/Profile';
import { ICONS } from 'labstep-web/constants/icons';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Link } from 'labstep-web/core/Link';
import type { ReportingColDef } from 'labstep-web/grid/Report/coldefs/types';
import { GridReportColumnHeader } from 'labstep-web/grid/Report/components/ColumnHeader';
import { GridReportService } from 'labstep-web/grid/Report/services/grid-report.service';
import {
  MAPPING_METADATA_TYPE_POST_FIELD,
  PostFilterFieldTypes,
} from 'labstep-web/grid/services/grid-filter.service';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { humanReadableDate } from 'labstep-web/services/date.service';

export const experimentNameColDef: ReportingColDef = {
  colId: `experiment_name`,
  sortable: true,
  headerName: 'Experiment',
  headerComponentParams: {
    icon: ICONS.experiment_workflow.primary,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.entity,
      path: 'experiment.experimentWorkflow',
      field: 'id',
      entityName: ExperimentWorkflow.entityName,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node?.data?.experiment?.experiment_workflow) {
      return '';
    }

    return GridReportService.getEntityFromState(
      params.node.data.experiment,
    ).experiment_workflow.displayName;
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node?.data?.experiment?.experiment_workflow) {
      return '';
    }

    const experiment = GridReportService.getEntityFromState(
      params.node.data.experiment,
    );

    return (
      <Link
        to="experiment_workflow_show"
        params={{
          id: experiment.experiment_workflow.guid,
        }}
        label={experiment.experiment_workflow.displayName}
      >
        {experiment.experiment_workflow.displayName}
      </Link>
    );
  },
};

export const experimentAuthorColDef: ReportingColDef = {
  colId: `experiment_author`,
  sortable: true,
  headerName: 'Created by',
  headerComponentParams: {
    icon: 'user circle',
    postFilterSettings: {
      path: 'experiment.experiment_workflow.author',
      field: 'name',
    },
  },
  field: 'experiment.experiment_workflow.author.name',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.experiment_workflow?.author?.name,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node?.data?.experiment?.experiment_workflow?.author) {
      return '';
    }
    return (
      <Profile
        user={params.node.data.experiment.experiment_workflow.author}
        viewComponent={
          <EntityLinkComponent
            entity={
              params.node.data.experiment.experiment_workflow.author
            }
          />
        }
      />
    );
  },
};

export const experimentWorkflowCreatedAtColDef: ReportingColDef = {
  colId: `experiment_workflow_created_at`,
  sortable: true,
  headerName: 'Experiment Creation Date',
  headerComponentParams: {
    icon: ICONS.experiment_workflow.primary,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      path: 'experiment.experimentWorkflow',
      field: 'created_at',
    },
  },
  field: 'experiment.experiment_workflow.created_at',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.experiment_workflow?.created_at,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (
      !params.node?.data?.experiment?.experiment_workflow?.created_at
    ) {
      return '';
    }
    return humanReadableDate(
      params.node.data.experiment.experiment_workflow.created_at,
    );
  },
};

export const experimentWorkflowStartedAtColDef: ReportingColDef = {
  colId: `experiment_workflow_started_at`,
  sortable: true,
  headerName: 'Experiment Start Date',
  headerComponentParams: {
    icon: 'calendar outline',
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      path: 'experiment.experimentWorkflow',
      field: 'started_at',
    },
  },
  field: 'experiment.experiment_workflow.started_at',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.experiment_workflow?.started_at,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (
      !params.node?.data?.experiment?.experiment_workflow?.started_at
    ) {
      return '';
    }
    return humanReadableDate(
      params.node.data.experiment.experiment_workflow.started_at,
    );
  },
};

export const experimentWorkflowEndedAtColDef: ReportingColDef = {
  colId: `experiment_workflow_ended_at`,
  sortable: true,
  headerName: 'Experiment Completion Date',
  headerComponentParams: {
    icon: 'calendar check outline',
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      path: 'experiment.experimentWorkflow',
      field: 'ended_at',
    },
  },
  field: 'experiment.experiment_workflow.ended_at',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.experiment_workflow?.ended_at,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (
      !params.node?.data?.experiment?.experiment_workflow?.ended_at
    ) {
      return '';
    }
    return humanReadableDate(
      params.node.data.experiment.experiment_workflow.ended_at,
    );
  },
};

export const experimentStartedAtColDef: ReportingColDef = {
  colId: `experiment_started_at`,
  sortable: true,
  headerName: 'Protocol Start Date',
  headerComponentParams: {
    icon: 'calendar outline',
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      path: 'experiment',
      field: 'started_at',
    },
  },
  field: 'experiment.started_at',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.started_at,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node?.data?.experiment?.started_at) {
      return '';
    }
    return humanReadableDate(params.node.data.experiment.started_at);
  },
};

export const experimentEndedAtColDef: ReportingColDef = {
  colId: `experiment_ended_at`,
  sortable: true,
  headerName: 'Protocol Completion Date',
  headerComponentParams: {
    icon: 'calendar check outline',
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      path: 'experiment',
      field: 'ended_at',
    },
  },
  field: 'experiment.ended_at',
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.ended_at,
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node?.data?.experiment?.ended_at) {
      return '';
    }
    return humanReadableDate(params.node.data.experiment.ended_at);
  },
};

export const experimentWorkflowEntityStateNameColDef: ReportingColDef =
  {
    colId: `experiment_workflow_entity_state_name`,
    sortable: true,
    headerName: 'Experiment Status',
    headerComponentParams: {
      icon: ICONS.entity_state_workflow.primary,
      postFilterSettings: {
        fieldType: PostFilterFieldTypes.text,
        path: 'experiment.experimentWorkflow.entityState',
        field: 'name',
      },
    },
    field: 'experiment.experiment_workflow.entity_state.name',
    valueGetter: (params: ValueGetterParams) =>
      params.node?.data?.experiment?.experiment_workflow?.entity_state
        ?.name,
    cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
      if (
        !params.node?.data?.experiment?.experiment_workflow
          ?.entity_state?.name
      ) {
        return '';
      }
      return params.node.data.experiment.experiment_workflow
        .entity_state.name;
    },
  };

export const protocolNameColDef: ReportingColDef = {
  colId: `protocol_name`,
  sortable: true,
  headerName: 'Protocol',
  headerComponentParams: {
    icon: ICONS.protocol_collection.primary,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.entity,
      path: 'experiment.protocol.collection',
      field: 'id',
      entityName: ProtocolCollection.entityName,
    },
  },
  valueGetter: (params: ValueGetterParams) =>
    params.node?.data?.experiment?.protocol?.collection
      ?.displayname || '',
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node?.data?.experiment?.protocol?.collection) {
      return '';
    }
    return (
      <Link
        to="protocol_collection_show"
        params={{
          id: params.node.data.experiment.protocol.collection.guid,
        }}
        label={
          params.node.data.experiment.protocol.collection.displayName
        }
      >
        {params.node.data.experiment.protocol.collection.displayName}
      </Link>
    );
  },
};

export const COL_ID_MAPPING: Record<string, ReportingColDef> = {
  experiment_name: experimentNameColDef,
  experiment_author: experimentAuthorColDef,
  experiment_workflow_created_at: experimentWorkflowCreatedAtColDef,
  experiment_workflow_started_at: experimentWorkflowStartedAtColDef,
  experiment_workflow_ended_at: experimentWorkflowEndedAtColDef,
  experiment_workflow_entity_state_name:
    experimentWorkflowEntityStateNameColDef,
  protocol_name: protocolNameColDef,
  experiment_started_at: experimentStartedAtColDef,
  experiment_ended_at: experimentEndedAtColDef,
};

export const getMetadataExperimentColDef = (
  type: MetadataType,
  metadataLabel: string,
): ReportingColDef => {
  return {
    colId: `metadata:experiment:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridReportColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.primary,
      postFilterSettings: {
        path: 'metadatas',
        field: MAPPING_METADATA_TYPE_POST_FIELD[type] || undefined,
        fieldType: PostFilterFieldTypes.metadata,
      },
    },
    valueGetter: (params: ValueGetterParams) =>
      !params.node
        ? ''
        : GridReportService.metadataValueGetter(
            [
              ...params.node.data.metadatas_constant,
              ...params.node.data.metadatas_variable,
            ],
            metadataLabel,
          ),
    cellRenderer: (params: CellRendererParams<ProtocolCondition>) =>
      GridReportService.metadataCellRenderer(
        [
          ...params.node.data.metadatas_constant,
          ...params.node.data.metadatas_variable,
        ],
        metadataLabel,
      ),
  };
};

export const getMetadataResourceItemColDef = (
  protocolValueName: string,
  type: MetadataType,
  metadataLabel: string,
) => ({
  colId: `metadata:resource_item:${type}:${metadataLabel}:${protocolValueName}`,
  sortable: true,
  headerName: metadataLabel,
  headerComponentParams: {
    icon: ICONS.metadata.secondary,
    cornerIcon: ICONS.resource_item.primary,
    secondaryHeader: `Item - ${protocolValueName}`,
    postFilterSettings: {
      label: metadataLabel,
      path: 'resourceItem.metadatas',
      field: MAPPING_METADATA_TYPE_POST_FIELD[type] || undefined,
      fieldType: PostFilterFieldTypes.metadata,
      protocolValueName: protocolValueName,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      protocolValueName,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }
    return GridReportService.metadataCellRenderer(
      resourceItem.metadata_thread.metadatas,
      metadataLabel,
    );
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      protocolValueName,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }

    return GridReportService.metadataCellRenderer(
      resourceItem.metadata_thread.metadatas,
      metadataLabel,
    );
  },
});

export const getMetadataResourceColDef = (
  protocolValueName: string,
  type: MetadataType,
  metadataLabel: string,
) => ({
  colId: `metadata:resource:${type}:${metadataLabel}:${protocolValueName}`,
  sortable: true,
  headerName: metadataLabel,
  headerComponentParams: {
    icon: ICONS.metadata.secondary,
    cornerIcon: ICONS.resource.primary,
    secondaryHeader: `Resource - ${protocolValueName}`,
    postFilterSettings: {
      label: metadataLabel,
      path: 'resourceItem.resource.metadatas',
      field: MAPPING_METADATA_TYPE_POST_FIELD[type] || undefined,
      fieldType: PostFilterFieldTypes.metadata,
      protocolValueName: protocolValueName,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValues = [];

    if (params.node?.data?.protocol_values_constant) {
      protocolValues.push(
        ...params.node.data.protocol_values_constant,
      );
    }
    if (params.node?.data?.protocol_values_variable) {
      protocolValues.push(
        ...params.node.data.protocol_values_variable,
      );
    }
    const foundProtocolValue = GridReportService.getProtocolValue(
      protocolValues,
      protocolValueName,
    );

    if (!foundProtocolValue || !foundProtocolValue.resourceItem) {
      return '';
    }
    return GridReportService.metadataCellRenderer(
      foundProtocolValue.resourceItem!.resource.metadata_thread
        .metadatas,
      metadataLabel,
    );
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      protocolValueName,
    );
    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }

    return GridReportService.metadataCellRenderer(
      resourceItem.resource.metadata_thread.metadatas,
      metadataLabel,
    );
  },
});

export const getProtocolValueFieldColumnDef = (
  isInput: boolean,
  name: string,
  field: string,
): ReportingColDef => ({
  colId: `protocol_value:${
    isInput ? 'true' : 'false'
  }:${name}:${field}`,
  sortable: true,
  headerName: 'Amount',
  headerComponentParams: {
    icon: ICONS.protocol_value.info.amount_used,
    secondaryHeader: name,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.numeric,
      field: 'amount',
      path: 'protocolValues',
      protocolValueName: name,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    return !params.node
      ? ''
      : GridReportService.protocolValueCellRenderer(
          [
            ...params.node.data.protocol_values_constant,
            ...params.node.data.protocol_values_variable,
          ],
          name,
          field,
        );
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) =>
    GridReportService.protocolValueCellRenderer(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
      field,
    ),
});

export const getProtocolValueResourceNameColumnDef = (
  isInput: boolean,
  name: string,
): ReportingColDef => ({
  colId: `protocol_value:${
    isInput ? 'true' : 'false'
  }:${name}:resource_name`,
  sortable: true,
  headerName: 'Resource',
  headerComponentParams: {
    icon: ICONS.resource.primary,
    secondaryHeader: name,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.entity,
      path: 'resourceItem.resource',
      field: 'id',
      entityName: Resource.entityName,
      protocolValueName: name,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }

    return resourceItem.resource.displayName;
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem?.resource) {
      return '';
    }

    return (
      <Link
        to="resource_show"
        params={{
          id: resourceItem?.resource.guid,
        }}
      >
        {resourceItem.resource.displayName}
      </Link>
    );
  },
});

export const getProtocolValueResourceItemNameColumnDef = (
  isInput: boolean,
  name: string,
): ReportingColDef => ({
  colId: `protocol_value:${
    isInput ? 'true' : 'false'
  }:${name}:resource_item_name`,
  sortable: true,
  headerName: 'Item',
  headerComponentParams: {
    icon: ICONS.resource_item.primary,
    secondaryHeader: name,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.entity,
      path: 'resourceItem',
      field: 'id',
      entityName: ResourceItem.entityName,
      protocolValueName: name,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    return resourceItem?.displayName || '';
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );

    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }

    return (
      <Link
        to="resource_item_show"
        params={{
          id: resourceItem.guid,
        }}
        label={resourceItem.displayName || ''}
      >
        {resourceItem.displayName}
      </Link>
    );
  },
});

export const getProtocolValueResourceLocationPathColumnDef = (
  isInput: boolean,
  name: string,
): ReportingColDef => ({
  colId: `protocol_value:${
    isInput ? 'true' : 'false'
  }:${name}:resource_location_path`,
  sortable: true,
  headerName: 'Location',
  headerComponentParams: {
    icon: ICONS.resource_location.primary,
    secondaryHeader: name,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.custom,
      field: 'name',
      path: 'resourceItem.resourceLocation',
      protocolValueName: name,
    },
  },
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );
    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem) {
      return '';
    }
    return resourceItem.resource_location?.location_path;
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const foundProtocolValue = GridReportService.getProtocolValue(
      [
        ...params.node.data.protocol_values_constant,
        ...params.node.data.protocol_values_variable,
      ],
      name,
    );
    const resourceItem = foundProtocolValue?.is_input
      ? foundProtocolValue?.resourceItem
      : foundProtocolValue?.resource_item_output;

    if (!resourceItem?.resource_location) {
      return '';
    }
    return (
      <Link
        to="resource_location_show"
        params={{
          id: resourceItem.resource_location.guid,
        }}
        label={resourceItem.resource_location.location_path || ''}
      >
        {resourceItem.resource_location.location_path}
      </Link>
    );
  },
});
